/**
 * Global loading overlay state.
 *
 * One full-screen overlay (components/GlobalLoadingOverlay.vue) covers the two
 * kinds of "the app is getting data" the user actually perceives:
 *
 *   - route navigations — lazy view chunks being fetched plus router guards
 *     doing async work (tracked as a boolean, because vue-router aborts and
 *     restarts navigations on redirect and a counter would leak)
 *   - a view's INITIAL data fetch — tracked as a counter via track(), so
 *     overlapping loads compose. Background re-polls are deliberately NOT
 *     tracked; a 15 s refresh must never flash an overlay over someone's work.
 *
 * The overlay stays for a minimum time once shown: a 60 ms fetch would
 * otherwise flash a frame of spinner, which reads as a glitch rather than
 * feedback. Hiding waits out the remainder; showing is always immediate.
 */
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

const MIN_VISIBLE_MS = 500;

export const useLoadingStore = defineStore('loading', () => {
  const dataPending = ref(0);
  const routePending = ref(false);
  const visible = ref(false);

  let shownAt = 0;
  let hideTimer: ReturnType<typeof setTimeout> | null = null;

  const anyPending = computed(() => routePending.value || dataPending.value > 0);

  function show(): void {
    if (hideTimer) {
      clearTimeout(hideTimer);
      hideTimer = null;
    }
    if (!visible.value) {
      visible.value = true;
      shownAt = Date.now();
    }
  }

  /** Hides once nothing is pending, honouring the minimum visible time. */
  function maybeHide(): void {
    if (anyPending.value || !visible.value) return;
    const remaining = MIN_VISIBLE_MS - (Date.now() - shownAt);
    if (remaining <= 0) {
      visible.value = false;
      return;
    }
    if (!hideTimer) {
      hideTimer = setTimeout(() => {
        hideTimer = null;
        if (!anyPending.value) visible.value = false;
      }, remaining);
    }
  }

  function routeStarted(): void {
    routePending.value = true;
    show();
  }

  function routeSettled(): void {
    routePending.value = false;
    maybeHide();
  }

  function begin(): void {
    dataPending.value += 1;
    show();
  }

  function end(): void {
    dataPending.value = Math.max(0, dataPending.value - 1);
    maybeHide();
  }

  /** Wraps a promise so the overlay covers exactly its lifetime. */
  async function track<T>(promise: Promise<T>): Promise<T> {
    begin();
    try {
      return await promise;
    } finally {
      end();
    }
  }

  return { visible, routeStarted, routeSettled, begin, end, track };
});
